import { Box, Fade, Grid, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { EmptyStateProps } from './EmptyState.types';

export const EmptyState = ({
  title,
  description,
  icon = '💡',
  iconSize = 55,
  iconColor,
  tip,
  tipTitle = '💡 İpucu',
  action,
  maxWidth = 520,
  minHeight = 300,
  padding = 2,
  variant = 'default',
  sx,
}: EmptyStateProps) => {
  const theme = useTheme();

  // Varsayılan açıklamalar
  const defaultDescriptions = useMemo(
    () => ({
      orders: 'Müşterilerinizden gelen siparişler burada listelenecek',
      products: 'Henüz hiç ürün eklenmemiş. Menünüzü oluşturmaya başlayın.',
      customers: 'Henüz hiç müşteri kaydı bulunmuyor.',
      default: 'Bu alanda henüz veri bulunmuyor.',
      custom: 'Bu alanda henüz veri bulunmuyor.',
    }),
    [],
  );

  // Varsayılan ipuçları
  const defaultTips = useMemo(
    () => ({
      orders:
        'Siparişler geldiğinde otomatik olarak burada görünecek. Müşterilerinizin sipariş verebilmesi için menünüzü güncel tutmayı unutmayın.',
      products:
        'Ürünlerinizi ekleyerek menünüzü oluşturabilirsiniz. Kategoriler oluşturarak düzenli bir menü yapısı kurabilirsiniz.',
      customers: 'Müşterileriniz sipariş verdiğinde burada listelenecek.',
      default: 'Veriler eklendiğinde burada görünecek.',
      custom: 'Veriler eklendiğinde burada görünecek.',
    }),
    [],
  );

  // İkon render fonksiyonu
  const renderIcon = () => {
    if (typeof icon === 'string') {
      return (
        <Typography
          sx={{
            fontSize: iconSize,
            color: iconColor || theme.palette.text.primary,
          }}
        >
          {icon}
        </Typography>
      );
    }
    return icon;
  };

  return (
    <Fade in timeout={600}>
      <Grid
        sx={{
          height: '100%',
          flex: 1,
          minHeight,
          padding,
          ...sx,
        }}
        justifyContent="center"
        alignItems="center"
        container
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth,
            px: 2,
          }}
        >
          {/* İkon */}
          <Grid justifyContent="center" mb={2} container>
            {renderIcon()}
          </Grid>

          {/* Başlık */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.primary,
              mb: 1,
              fontSize: '1.25rem',
            }}
          >
            {title}
          </Typography>

          {/* Açıklama */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.5,
              mb: 3,
              fontSize: '0.875rem',
            }}
          >
            {description || defaultDescriptions[variant]}
          </Typography>

          {/* İpucu */}
          {(tip || defaultTips[variant]) && (
            <Box
              sx={{
                backgroundColor: theme.palette.action.hover,
                borderRadius: 2,
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                opacity: 0.8,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  display: 'block',
                  mb: 1,
                }}
              >
                {tipTitle}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.4,
                  fontSize: '0.75rem',
                }}
              >
                {tip || defaultTips[variant]}
              </Typography>
            </Box>
          )}

          {/* Aksiyon */}
          {action && <Box sx={{ mt: 2 }}>{action}</Box>}
        </Box>
      </Grid>
    </Fade>
  );
};
